import { z } from 'zod';
// ---------------------------------------------------------------------------
// Questionnaire-Engine — RA-definierte, fallspezifische Fragebögen.
// Mehrsprachige Labels (de/en/ar/fr); weitere Sprachen = nur zusätzlicher Key.
// Definition wird in JurNodes/DB als JSONB gehalten; Antworten landen im
// onboarding_draft (Feld `fragebogen`) und beim Submit in der Lead-/Akten-Notiz.
// ---------------------------------------------------------------------------
export const LOCALIZED_LANGS = ['de', 'en', 'ar', 'fr'];
export const localizedText = z
    .object({
    de: z.string().max(2000).optional(),
    en: z.string().max(2000).optional(),
    ar: z.string().max(2000).optional(),
    fr: z.string().max(2000).optional(),
})
    .refine((o) => Object.values(o).some((v) => typeof v === 'string' && v.trim().length > 0), {
    message: 'mindestens eine Sprache muss gesetzt sein',
});
export const questionType = z.enum([
    'text',
    'textarea',
    'select',
    'radio',
    'checkbox',
    'date',
    'number',
    'heading',
]);
export const questionOption = z.object({
    value: z.string().min(1).max(100),
    label: localizedText,
});
export const questionnaireQuestion = z.object({
    id: z.string().regex(/^[a-zA-Z0-9_]{1,64}$/, 'id: nur a-z A-Z 0-9 _'),
    type: questionType,
    label: localizedText,
    help: localizedText.optional(),
    options: z.array(questionOption).max(50).optional(),
    required: z.boolean().optional().default(false),
    section: z.string().max(120).optional(),
});
export const questionnaireDefinition = z.array(questionnaireQuestion).max(300);
/** Antworten: questionId → Wert. string (text/select/radio/date/number),
 *  boolean (einzelne checkbox) oder string[] (Mehrfach-checkbox). */
export const questionnaireAnswers = z.record(z.string().max(64), z.union([z.string().max(20_000), z.boolean(), z.array(z.string().max(2000)).max(100)]));
/** Antwort von GET /questionnaire — der dem User zugewiesene Fragebogen. */
export const questionnaireResponse = z.object({
    slug: z.string(),
    title: localizedText,
    description: localizedText.optional(),
    defaultLang: z.enum(LOCALIZED_LANGS),
    questions: questionnaireDefinition,
});
// Registrierung — Schritt 1, vor Double-Opt-In
export const registerInput = z.object({
    vorname: z.string().trim().min(1).max(100),
    nachname: z.string().trim().min(1).max(100),
    geburtsdatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    email: z.string().trim().toLowerCase().email().max(254),
    einwilligung: z.literal(true, { errorMap: () => ({ message: 'Einwilligung erforderlich' }) }),
    turnstileToken: z.string().min(1),
    // Honeypot — muss leer bleiben; Bots füllen es aus
    website: z.string().max(0).optional().default(''),
});
// Login (Magic-Link)
export const loginInput = z.object({
    email: z.string().trim().toLowerCase().email().max(254),
    turnstileToken: z.string().min(1),
});
// Onboarding-Draft — wird mehrstufig befüllt
export const onboardingDraft = z.object({
    sachverhalt: z.string().max(20_000).optional(),
    rechtsgebiet: z.enum([
        'arbeitsrecht',
        'mietrecht',
        'familienrecht',
        'verkehrsrecht',
        'strafrecht',
        'kartellrecht',
        'vergaberecht',
        'verwaltungsrecht',
        'sonstiges',
    ]).optional(),
    gegenseite: z.object({
        name: z.string().max(200),
        anschrift: z.string().max(500).optional(),
        vertreten: z.string().max(200).optional(),
    }).optional(),
    vollmachtErteilt: z.boolean().optional(),
    // Antworten auf den fallspezifischen Fragebogen (sofern dem User zugewiesen).
    fragebogen: questionnaireAnswers.optional(),
    uploads: z.array(z.object({
        id: z.string().uuid(),
        filename: z.string().max(255),
        mimetype: z.string().max(100),
        size: z.number().int().positive(),
        kategorie: z.enum(['ausweis', 'vollmacht', 'korrespondenz', 'beweis', 'sonstiges', 'nachreichung']),
    })).default([]),
});
export const submitInput = onboardingDraft.required({
    sachverhalt: true,
    rechtsgebiet: true,
    vollmachtErteilt: true,
}).extend({
    bestaetigungVollstaendig: z.literal(true),
});
// Fall-Intake-Submit (einladungsbasierter, fallspezifischer Flow /intake) —
// GETRENNT von der generischen Registrierung: verlangt Fragebogen-Antworten +
// Vollmacht, NICHT Sachverhalt/Rechtsgebiet/Gegenseite.
export const caseIntakeSubmit = onboardingDraft.extend({
    vollmachtErteilt: z.literal(true),
    bestaetigungVollstaendig: z.literal(true),
}).refine((d) => !!d.fragebogen && Object.keys(d.fragebogen).length > 0, { message: 'Fragebogen-Antworten erforderlich', path: ['fragebogen'] });
// "Me" — was die Frontend-App über eingeloggten User wissen darf
export const meResponse = z.object({
    id: z.string().uuid(),
    vorname: z.string(),
    nachname: z.string(),
    email: z.string().email(),
    verifiedAt: z.string().nullable(),
    hasDraft: z.boolean(),
    submitted: z.boolean(),
    /** True, wenn der User submittet hat ODER einen Bookings-Termin hat. */
    canUploadNachreichung: z.boolean(),
    /** Anzahl bereits hochgeladener Nachreichungen — fürs Dashboard-Badge. */
    nachreichungCount: z.number().int().nonnegative(),
    /** True, wenn dem User ein fallspezifischer Fragebogen zugewiesen ist
     *  → Dashboard/Flow routen dann nach /intake statt /onboarding. */
    hasQuestionnaire: z.boolean(),
});
// Upload-Metadaten — Antwort von POST /uploads und Liste im Draft
// Wizard-Uploads: ausweis | vollmacht | korrespondenz | beweis | sonstiges
// Dashboard-Nachreichungen (post-submit / nach Bookings-Termin): nachreichung
export const uploadKategorie = z.enum([
    'ausweis',
    'vollmacht',
    'korrespondenz',
    'beweis',
    'sonstiges',
    'nachreichung',
]);
export const uploadMeta = z.object({
    id: z.string().uuid(),
    filename: z.string().max(255),
    mimetype: z.string().max(100),
    size: z.number().int().nonnegative(),
    kategorie: uploadKategorie,
    createdAt: z.string(),
});
// Draft-Response: bestehender Stand + Datei-Liste + Submit-Status
export const draftResponse = z.object({
    draft: onboardingDraft,
    uploads: z.array(uploadMeta),
    submitted: z.boolean(),
    submittedAt: z.string().nullable(),
    jurnodesLeadId: z.string().nullable(),
    status: z.string(),
});
// Submit-Response
export const submitResponse = z.object({
    ok: z.literal(true),
    jobId: z.string(),
    status: z.literal('queued'),
});
export const leadStatus = z.enum(['ungeprueft', 'angenommen', 'abgelehnt', 'gepusht', 'fehler']);
export const leadSource = z.enum(['self-service', 'einladung']);
// Nachreichungen — Antwort von POST /uploads/nachreichung und GET /uploads/nachreichung
export const nachreichungListResponse = z.object({
    canUpload: z.boolean(),
    /** Warum sich das canUpload-Flag entschieden hat (für Hint im UI). */
    reason: z.enum(['submitted', 'has_booking', 'both', 'none']),
    uploads: z.array(uploadMeta),
});
export const nachreichungUploadResponse = z.object({
    uploads: z.array(uploadMeta),
    jobId: z.string().nullable(),
});
// DB-Portal (TAB-Vergleichsdaten, Datenlieferung)
export * from './db-portal.js';
export * from './db-portal-lieferung.js';
export * from './db-portal-verwaltung.js';
export * from './db-portal-analyse.js';
//# sourceMappingURL=index.js.map